<script lang="ts">
export default {
  name: 'ExternalPolicyActionPanel',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Loader, Plus, Trash2 } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';
import ExternalPolicyTargetSummary from '@/components/v2/external_policy_upload/ExternalPolicyTargetSummary.vue';
import { externalPolicyBinderService } from '@/services/v2/ExternalPolicyBinderService';
import { useToast } from '@/composables/useToast';
import type { EntityMatch, ExtractionTarget } from '@/types/ai';

// The commit surface for an extraction that creates a policy: the target summary plus the
// actions that resolve the submission. Only the account-scoped review route mounts this --
// the AI surfaces render the summary alone, since they do not enforce account access.
const props = defineProps<{
  accountId: string | number;
  target: ExtractionTarget;
  entityMatches: EntityMatch[];
  entityValues?: Record<string, string>;
  // Edits sitting unsaved in the field list. Creating now would build the policy from the
  // extraction without them, so the create waits until they are saved.
  pendingEditCount?: number;
}>();

const emit = defineEmits<{
  created: [];
  discarded: [];
}>();

const toast = useToast();
const isCreating = ref(false);
const isDiscarding = ref(false);
const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null);
const errors = ref<string[]>([]);

const hasUnsavedEdits = computed(() => (props.pendingEditCount ?? 0) > 0);

async function createPolicy() {
  errors.value = [];
  isCreating.value = true;
  try {
    // Every correction is a saved review of the extraction by this point, so the server
    // folds them in and builds the record itself.
    const response = await externalPolicyBinderService.confirmSubmission(props.accountId, props.target.submissionId!);
    if (!response.validPolicy) {
      errors.value = response.messages || ['The extracted policy could not be created'];
      return;
    }
    toast.success('External policy added.');
    emit('created');
  } catch (err) {
    errors.value = [(err as Error).message || 'Creating the policy failed. Please try again.'];
  } finally {
    isCreating.value = false;
  }
}

async function discard() {
  const confirmed = await confirmModal.value?.showConfirm(
    'Discard this extraction? The binder stays on the account and can be scanned again, and no policy is created.',
  );
  if (!confirmed) {
    return;
  }

  isDiscarding.value = true;
  try {
    await externalPolicyBinderService.discardSubmission(props.accountId, props.target.submissionId!);
    toast.success('Extraction discarded.');
    emit('discarded');
  } catch (err) {
    toast.error((err as Error).message || 'Discarding failed. Please try again.');
  } finally {
    isDiscarding.value = false;
  }
}
</script>

<template>
  <div
    class="shrink-0 rounded-lg border border-slate-200 bg-white/80 p-3 backdrop-blur-sm"
    data-testid="external-policy-action-panel">
    <ExternalPolicyTargetSummary
      :target="target"
      :entity-matches="entityMatches"
      :entity-values="entityValues" />

    <div
      v-if="errors.length > 0"
      class="mt-3 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-700"
      data-testid="external-policy-create-errors">
      <p class="font-medium">Could not create the policy:</p>
      <ul class="mt-1 list-inside list-disc">
        <li
          v-for="(error, index) of errors"
          :key="index">
          {{ error }}
        </li>
      </ul>
    </div>

    <p
      v-if="hasUnsavedEdits"
      class="mt-3 text-xs text-amber-700"
      data-testid="unsaved-edits-hint">
      Save your changes in the extracted fields before creating the policy.
    </p>

    <div class="mt-3 flex items-center justify-between gap-2">
      <AppButton
        secondary
        :disabled="isCreating || isDiscarding"
        data-testid="discard-extraction"
        @click="discard">
        <Trash2 class="mr-1 h-4 w-4" />
        Discard
      </AppButton>
      <AppButton
        :disabled="isCreating || isDiscarding || hasUnsavedEdits"
        data-testid="create-external-policy"
        @click="createPolicy">
        <Loader
          v-if="isCreating"
          class="mr-1 h-4 w-4 animate-spin" />
        <Plus
          v-else
          class="mr-1 h-4 w-4" />
        Create policy
      </AppButton>
    </div>

    <ConfirmModal ref="confirmModal" />
  </div>
</template>
